import type { FetchResponse, Reference, RestService } from '@sydle/ui-api';
import { buildRequestParams } from '@sydle/ui-api';
import { FILE_CLASS_ID } from './constants';

// To do: models
export enum ExecutionContext {
  OBJECT = 'OBJECT',
  CLASS = 'CLASS',
}

export type ConversionOption = {
  contentType: string;
  extension: string;
  label: string;
};

export type BlobInfo = {
  blobUrl: string;
  contentType: string;
};

export type StartUploadResponse = {
  uploadUrl: string;
  completeUrl: string;
  uploadId: string;
};

export class FileAPI {
  private static CACHE: Map<string, Promise<FetchResponse<BlobInfo>>> = new Map<
    string,
    Promise<FetchResponse<BlobInfo>>
  >();

  private restService: RestService;

  constructor(restService: RestService) {
    this.restService = restService;
  }

  transform(url: string, ttl: number = 1000 * 60 * 60 * 24): Promise<FetchResponse<BlobInfo>> {
    return this.load(url, ttl);
  }

  getFileNameFromResponseHeaders(response: FetchResponse<BlobInfo>): string {
    const contentDisposition = response.meta.response.headers.get('content-disposition');
    if (!contentDisposition) {
      return '';
    }
    let filename = '';

    const filenameRegex = /filename\*=UTF-8''(.+?)(?:;|$)/i;
    const match = contentDisposition.match(filenameRegex);

    if (match?.[1]) {
      filename = decodeURIComponent(match[1]);
    } else {
      const fallbackFilenameRegex = /filename="([^";]+)/;
      const fallbackMatch = contentDisposition.match(fallbackFilenameRegex);

      if (fallbackMatch?.[1]) {
        filename = fallbackMatch[1];
      }
    }

    return filename;
  }

  getConversionOptions(contentType: string): Promise<FetchResponse<ConversionOption[]>> {
    const requestParams = buildRequestParams({
      _classId: FILE_CLASS_ID,
      methodIdentifier: 'getFileFormatToConvert',
      body: {
        contentType,
      },
    });
    return this.restService.post(requestParams);
  }

  getXHR({ _classId, _id }: Reference): XMLHttpRequest {
    const requestParams = buildRequestParams({ _classId, _id, methodIdentifier: '_upload' });
    return this.restService.buildXHR(requestParams);
  }

  getChunkXHR({ classId, objectId }): XMLHttpRequest {
    const requestParams = buildRequestParams({ _classId: classId, _id: objectId, methodIdentifier: 'uploadChunk' });
    return this.restService.buildXHR(requestParams);
  }

  startMultipartUpload(classId: string, objectId: string): Promise<FetchResponse<StartUploadResponse>> {
    return this.restService.post(
      buildRequestParams({
        _classId: classId,
        _id: objectId,
        methodIdentifier: 'startUpload',
      }),
    );
  }

  completeMultipartUpload(
    classId: string,
    objectId: string,
    headers: { 'X-upload-session-id': string },
  ): Promise<FetchResponse<StartUploadResponse>> {
    return this.restService.post({
      url: `_classId/${classId}/completeUpload/${objectId}`,
      headers,
    });
  }

  cancelMultipartUpload(
    classId: string,
    objectId: string,
    headers: { 'X-upload-session-id': string },
  ): Promise<FetchResponse<StartUploadResponse>> {
    return this.restService.delete({
      url: `_classId/${classId}/completeUpload/${objectId}`,
      headers,
    });
  }

  private async load(url: string, ttl: number): Promise<FetchResponse<BlobInfo>> {
    if (url == null || url === '') {
      return { error: { status: null, data: 'Missing URL.' } };
    }
    let blobPromise = FileAPI.CACHE.get(url);
    if (blobPromise != null) {
      return blobPromise;
    }

    blobPromise = this.loadBlob(url);

    if (ttl > 0) {
      FileAPI.CACHE.set(url, blobPromise);
      setTimeout(() => {
        FileAPI.CACHE.delete(url);
        URL.revokeObjectURL(url);
      }, ttl);
    }

    const blob = await blobPromise;
    if (blob.error) {
      FileAPI.CACHE.delete(url);
    }

    return blob;
  }

  private async loadBlob(url: string): Promise<FetchResponse<BlobInfo>> {
    const response = await this.restService.get({
      url,
      responseContentType: 'blob',
      additionalParams: { suppressAlerts: true },
    });

    if (response.error) {
      return response;
    }
    const contentType = response.meta.response.headers.get('content-type');
    return { ...response, error: undefined, data: { blobUrl: this.getBlobUrl(response.data), contentType } };
  }

  private getBlobUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }
}
